import Axios from "axios";
import store from "../store";

import { EnvironmentConstants } from "../environment";
import { parseISO, subHours, startOfDay, format, startOfMonth } from "date-fns";

const APP_SOURCE = "GMPCorpSite";
const CLIENT_ID = "C978562571FC475294191C7B94DD883E";

const EXPERIAN_URL = "https://api.experianmarketingservices.com";
const EXPERIAN_TOKEN = "8354e97c-e353-448f-ab52-6b31f83f1cce";

const WEBCHAT_URL = "https://chat.greenmountainpower.com:8085/webchat/client";

const SOURCE_HEADER = {
  "GMP-Source": "web",
};

export default class GMPAPI {
  /** Make an unauthed call to the GMP API */
  static async GMPCallAnonymous(method, route, data) {
    const response = await Axios({
      method,
      url: EnvironmentConstants.GmpApiURL + route,
      data,
      headers: SOURCE_HEADER,
    });
    return response.data;
  }

  /** Make a call to the GMP API with the user auth token */
  static async GMPCallAuthed(method, route, data) {
    let headers = SOURCE_HEADER;
    const token = store.state.user.apitoken;
    if (token) {
      headers = {
        ...SOURCE_HEADER,
        Authorization: "Bearer " + token,
      };
    }
    const response = await Axios({
      method,
      url: EnvironmentConstants.GmpApiURL + route,
      data,
      headers,
      withCredentials: true,
    });
    return response.data;
  }

  /** Some GMP API calls return 404s instead of empty arrays etc when there are no records. This fixes that. */
  static async GMPCallAuthedWith404Subst(method, route, data) {
    try {
      const response = await this.GMPCallAuthed(method, route, data);
      return response;
    } catch (err) {
      if (err.response && err.response.status === 404) {
        return [];
      } else {
        throw err;
      }
    }
  }

  static async GetTokenUsername(username, password, rememberme) {
    const params = new URLSearchParams();
    params.append("username", username);
    params.append("password", password);
    params.append("client_id", CLIENT_ID);
    const response = await Axios.post(EnvironmentConstants.GmpApiURL + "/applications/token?remember_me=" + rememberme, params, { headers: SOURCE_HEADER, withCredentials: true });
    return response.data;
  }

  static async GetRefreshedToken(refreshToken, rememberme) {
    const params = new URLSearchParams();
    params.append("grant_type", "refresh_token");
    params.append("refresh_token", refreshToken);
    params.append("client_id", CLIENT_ID);
    const response = await Axios.post(EnvironmentConstants.GmpApiURL + "/applications/token?remember_me=" + rememberme, params, { headers: SOURCE_HEADER, withCredentials: true });
    return response.data;
  }

  static async RevokeRefreshToken(refreshToken) {
    try {
      const params = new URLSearchParams();
      params.append("refresh_token", refreshToken);
      params.append("client_id", CLIENT_ID);
      const response = await Axios.post(EnvironmentConstants.GmpApiURL + "/applications/token/revoke", params, { headers: SOURCE_HEADER, withCredentials: true });
    } catch (err) {
      // Don't worry about it
    }
  }

  static async GetTokenOTP(accountNumber, phoneLast) {
    const params = new URLSearchParams();
    params.append("grant_type", "account_phone");
    params.append("account_number", accountNumber);
    params.append("phone_number", phoneLast);
    params.append("client_id", CLIENT_ID);
    const response = await Axios.post(EnvironmentConstants.GmpApiURL + "/applications/token?remember_me=false", params, { headers: SOURCE_HEADER });
    return response.data;
  }

  static async LogoutCookie() {
    try {
      await this.GMPCallAuthed("get", "/applications/logout");
    } catch (err) {
      // Don't worry about it
    }
  }

  /** Download the given data as a file */
  static DownloadFile(filename, data) {
    const url = window.URL.createObjectURL(new Blob([data]));
    const link = document.createElement("a");
    link.href = url;
    link.setAttribute("download", filename);
    document.body.appendChild(link);
    link.click();
  }

  // Not logged in
  static async VerifyAccountPhone(accountNumber, phoneLast) {
    await this.GMPCallAnonymous("put", "/accounts/" + accountNumber + "/verify", { accountNumber, value: phoneLast, type: "phone" });
  }
  static async GetIncidentsSummary() {
    return await this.GMPCallAnonymous("get", "/outages/incidents/summary");
  }
  static async GetOutageForecast() {
    return await this.GMPCallAnonymous("get", "/outages/forecast");
  }
  static async GetPaymentMeta() {
    return await this.GMPCallAnonymous("get", "/payments/meta");
  }

  // Note: the next 4 calls should probably be made via GMPCallAnonymous, but have already gone through testing
  static async CreateUser(body) {
    return await this.GMPCallAuthed("post", "/users", body);
  }
  static async ResetPassword(email) {
    return await this.GMPCallAuthed("post", "/users/" + encodeURIComponent(email) + "/password/reset");
  }
  static async UpdateResetPassword(token, password) {
    return await this.GMPCallAuthed("post", "/users/password/update", { token, newPassword: password });
  }
  static async ForgotUsername(email) {
    return await this.GMPCallAuthed("post", "/users/" + encodeURIComponent(email) + "/forgot-username");
  }
  static async GetEbillToken(token) {
    return await this.GMPCallAnonymous("get", "/users/ebill/contacts?token=" + encodeURIComponent(token));
  }
  static async SubmitEbillToken(token, accounts) {
    return await this.GMPCallAnonymous("post", "/accounts/ebill/enroll", { token, accounts });
  }
  static async VerifyContact(token) {
    await this.GMPCallAnonymous("post", "/alerts/contacts/verify?token=" + token);
  }

  // User info
  static async GetCurrentUser() {
    const current = await this.GMPCallAuthed("get", "/users/current");
    if (current && current.groups && current.groups.length) {
      const admin = current.groups.find(item => item.name === "CSS_ADMIN");
      const veic = current.groups.find(item => item.name === "VEIC_USER");
      if (admin) {
        current.isAdmin = true;
      }
      if(veic) {
        current.isAdminLite = true;
      }
    }
    if (current.username.startsWith("TEMP_ACCOUNT_")) {
      current.isOTP = true;
    }
    return current;
  }
  static async GetAccounts(accountNumbers) {
    const numberList = accountNumbers.join(",");
    return await this.GMPCallAuthed("get", "/accounts/list?accounts=" + numberList);
  }

  // Payment
  static async GetWallets(accountNumber) {
    const wallets = await this.GMPCallAuthedWith404Subst("get", "/payments/account/" + accountNumber + "/wallet?checkRecurringSchedule=true");
    const expiryCutoff = startOfMonth(new Date());
    for (const wallet of wallets) {
      if (wallet.expirationDate && wallet.expirationDate.length >= 8) {
        try {
          const parsed = parseISO(wallet.expirationDate);
          // Check for expired cards and reformat expiration date
          if (parsed < expiryCutoff) {
            wallet.isExpired = true;
          }
          wallet.expirationDate = format(parsed, "MM/yy");
        } catch (err) {
          console.error("Invalid expiration date: " + wallet.expirationDate);
        }
      }
    }
    return wallets;
  }
  /** Returns the id of the created wallet */
  static async CreateWallet(accountNumber, walletRecord) {
    const body = {
      source: APP_SOURCE,
      walletRecord,
    };
    const result = await this.GMPCallAuthed("post", "/payments/account/" + accountNumber + "/wallet", body);
    return result.walletId;
  }
  static async UpdateWallet(accountNumber, walletRecord, walletId) {
    const body = {
      source: APP_SOURCE,
      walletRecord,
    };
    return await this.GMPCallAuthed("put", "/payments/account/" + accountNumber + "/wallet/" + walletId, body);
  }
  static async DeleteWallet(accountNumber, walletId) {
    return await this.GMPCallAuthed("delete", "/payments/account/" + accountNumber + "/wallet/" + walletId + "?source=" + APP_SOURCE + "&checkRecurringSchedule=true");
  }
  static async GetCardIsATM(tokenizedCard) {
    return await this.GMPCallBillPay("get", "/payments/card/" + tokenizedCard + "/binfile");
  }
  static async MakePayment(accountNumber, paymentInsertRecord) {
    const body = {
      source: APP_SOURCE,
      paymentInsertRecord,
    };
    return await this.GMPCallAuthed("post", "/payments/" + accountNumber + "/payment", body);
  }
  static async GetRecurring(accountNumber) {
    return await this.GMPCallAuthedWith404Subst("get", "/payments/account/" + accountNumber + "/recurring");
  }
  static async SetRecurring(accountNumber, walletId, emailAddress, customerInformation, debitInformation) {
    const body = {
      source: APP_SOURCE,
      walletId,
      insertRecord: {
        customerInformation,
        recurringScheduleInformation: {
          emailAddress,
        },
        debitInformation,
      },
    };
    return await this.GMPCallAuthed("post", "/payments/account/" + accountNumber + "/recurring", body);
  }
  static async DeleteRecurring(accountNumber, recurringScheduleId) {
    return await this.GMPCallAuthed("delete", "/payments/account/" + accountNumber + "/recurring/" + recurringScheduleId + "?source=" + APP_SOURCE);
  }

  // Usage
  static async GetPeriodUsage(accountNumber, interval, startDate, endDate) {
    const result = await this.GMPCallAuthed("get", "/usage/" + accountNumber + "/" + interval + "?startDate=" + startDate + "&endDate=" + endDate + "&temp=f");
    if (result && result.intervals && result.intervals.length && result.intervals[0].values && result.intervals[0].values.length) {
      for (const datapoint of result.intervals[0].values) {
        datapoint.date = this.FixUsageDate(datapoint.date, interval);
      }
      // Don't allow returned dates from today or later
      const today = startOfDay(new Date());
      result.intervals[0].values = result.intervals[0].values.filter(item => item.date < today);
    }
    return result;
  }
  /** Usage API returns incorrectly formatted / offset dates */
  static FixUsageDate(date, interval) {
    // The usage API always returns "UTC" but the actual times are VT local
    let parsed = parseISO(date.slice(0, -1));
    if (interval === "hourly") {
      parsed = subHours(parsed, 1);
    }
    return parsed;
  }

  static async DownloadUsageExport(accountNumber, filename, startDate, endDate, format) {
    const contents = await this.GMPCallAuthed("get", "/usage/" + accountNumber + "/download?startDate=" + startDate + "&endDate=" + endDate + "&format=" + format);
    this.DownloadFile(filename, contents);
  }

  // Billing
  static async GetBillingPeriods(accountNumber) {
    return await this.GMPCallAuthed("get", "/accounts/" + accountNumber + "/billing/periods");
  }
  static async GetAccountTransactions(accountNumber, startDate, endDate) {
    const transactions = await this.GMPCallAuthedWith404Subst("get", "/accounts/" + accountNumber + "/transactions?startDate=" + startDate + "&endDate=" + endDate);
    // Convert to JS dates
    for (const transaction of transactions) {
      transaction.date = parseISO(transaction.date);
    }
    return transactions;
  }
  /** Note: ignore speedpayAutoPayEnrolled, it is not accurate. Instead call GetRecurring */
  static async GetBillingStatus(accountNumber) {
    return await this.GMPCallAuthed("get", "/accounts/" + accountNumber + "/billing");
  }
  static async UpdateBillingStatus(accountNumber, props) {
    return await this.GMPCallAuthed("put", "/accounts/" + accountNumber + "/billing?fields=billRouteInformation", props);
  }
  static async GetBudgetStatus(accountNumber) {
    return await this.GMPCallAuthed("get", "/accounts/" + accountNumber + "/billing/budget");
  }
  static async CreateBudgetBilling(accountNumber) {
    return await this.GMPCallAuthed("post", "/accounts/" + accountNumber + "/billing/budget", {});
  }
  static async GetBillingHistory(accountNumber, daysBackToSearch) {
    let query = "";
    if (daysBackToSearch) {
      query = "?daysBackToSearch=" + daysBackToSearch;
    }
    return await this.GMPCallAuthedWith404Subst("get", "/payments/" + accountNumber + "/history" + query);
  }

  // Alerts
  static async GetAlertContacts(accountNumber) {
    return await this.GMPCallAuthedWith404Subst("get", "/alerts/accounts/" + accountNumber + "/contacts?expandAll=true");
  }
  static async GetAlertTypes(accountNumber) {
    const result = await this.GMPCallAuthed("get", "/alerts/accounts/" + accountNumber + "/alert-types");
    return result.alertTypes;
  }
  static async GetAlertChannels() {
    return await this.GMPCallAuthed("get", "/alerts/channels");
  }
  static async SubscribeAlert(accountNumber, contactId, alertTypeId) {
    return await this.GMPCallAuthed("post", "/alerts/accounts/" + accountNumber + "/subscriptions", { contact: { contactId }, alertType: { alertTypeId } });
  }
  static async UnSubscribeAlert(accountNumber, subscriptionId) {
    return await this.GMPCallAuthed("delete", "/alerts/accounts/" + accountNumber + "/subscriptions/" + subscriptionId);
  }
  static async CreateContact(accountNumber, body) {
    return await this.GMPCallAuthed("post", "/alerts/accounts/" + accountNumber + "/contacts", body);
  }
  static async UpdateContact(accountNumber, contactId, body) {
    return await this.GMPCallAuthed("put", "/alerts/accounts/" + accountNumber + "/contacts/" + contactId, body);
  }
  static async DeleteContact(accountNumber, contactId) {
    return await this.GMPCallAuthed("delete", "/alerts/accounts/" + accountNumber + "/contacts/" + contactId);
  }
  static async ResendContactVerification(accountNumber, contactId) {
    return await this.GMPCallAuthed("post", "/alerts/accounts/" + accountNumber + "/contacts/" + contactId + "/resend", {});
  }

  // Outages
  static async GetAccountIncidents(accountNumber) {
    return await this.GMPCallAuthedWith404Subst("get", "/outages/incidents/account/" + accountNumber);
  }
  static async GetReportedIncident(accountNumber) {
    return await this.GMPCallAuthed("get", "/outages/accounts/" + accountNumber + "/reported");
  }
  static async GetAllIncidents() {
    return await this.GMPCallAuthedWith404Subst("get", "/outages/incidents?active=true");
  }

  // Account
  static async UpdateNickname(username, accountNumber, nickname) {
    return await this.GMPCallAuthed("post", "/users/" + encodeURIComponent(username) + "/accounts/" + accountNumber, { nickname });
  }
  static async SetPrimary(username, accountNumber) {
    return await this.GMPCallAuthed("post", "/users/" + encodeURIComponent(username) + "/accounts/" + accountNumber, { isPrimary: true });
  }
  static async AddUserAccount(username, accountNumber, phoneLastFour, nickname) {
    return await this.GMPCallAuthed("put", "/users/" + encodeURIComponent(username) + "/accounts/" + accountNumber, { accountNumber, nickname, accountVerifyValue: phoneLastFour, accountVerifyType: "phone" });
  }
  static async UpdateUser(username, fields) {
    return await this.GMPCallAuthed("post", "/users/" + encodeURIComponent(username), fields);
  }
  static async RemoveUserAccount(username, accountNumber) {
    return await this.GMPCallAuthed("delete", "/users/" + encodeURIComponent(username) + "/accounts/" + accountNumber);
  }
  static async DeleteUsername(username) {
    return await this.GMPCallAuthed("delete", "/users/" + encodeURIComponent(username));
  }
  static async GetImageBlob(url) {
    const response = await Axios({
      method: "get",
      url,
      responseType: "blob",
    });
    const filename = url.substring(url.lastIndexOf("/") + 1);
    return new File([new Blob([response.data])], filename, { type: response.headers["content-type"] });
  }
  static async UpdatePhoto(username, accountNumber, photo) {
    const formData = new FormData();
    formData.append("file", photo);
    return await this.GMPCallAuthed("post", "/users/" + encodeURIComponent(username) + "/accounts/" + accountNumber + "/photo", formData);
  }
  static async GetPhoneNumbers(accountNumber) {
    return await this.GMPCallAuthed("get", "/accounts/" + accountNumber + "/phones");
  }
  static async UpdatePhoneNumbers(accountNumber, personId, phones) {
    const params = {
      accountNumber,
      personId,
      phones,
    };
    return await this.GMPCallAuthed("put", "/accounts/" + accountNumber + "/phones", params);
  }
  static async GetMailingAddress(accountNumber) {
    return await this.GMPCallAuthed("get", "/accounts/" + accountNumber + "/address/mailing");
  }
  static async UpdateMailingAddress(accountNumber, address) {
    return await this.GMPCallAuthed("put", "/accounts/" + accountNumber + "/address/mailing", address);
  }
  static async GetPaymentArrangement(accountNumber) {
    return await this.GMPCallAuthed("get", "/accounts/" + accountNumber + "/arrangement");
  }
  static async UpdatePaymentArrangement(accountNumber, type) {
    return await this.GMPCallAuthed("put", "/accounts/" + accountNumber + "/arrangement", { selectedArrangementType: type });
  }
  static async GetNetMetering(accountNumber) {
    return await this.GMPCallAuthedWith404Subst("get", "/accounts/" + accountNumber + "/generation/credits");
  }
  static async GetAccountStatus(accountNumber) {
    return await this.GMPCallAuthed("get", "/accounts/" + accountNumber + "/status");
  }
  // Stop service
  static async GetHolidays() {
    return await this.GMPCallAuthed("get", "/accounts/holidays");
  }
  static async GetStopEligible(accountNumber) {
    return await this.GMPCallAuthed("get", "/accounts/" + accountNumber + "/service");
  }
  static async ScheduleStopService(accountNumber, body) {
    return await this.GMPCallAuthed("post", "/accounts/" + accountNumber + "/service", body);
  }

  // Admin
  static async SearchAccounts(searchText) {
    return await this.GMPCallAuthedWith404Subst("get", "/accounts?q=" + encodeURI(searchText));
  }

  // External calls
  static async TokenizeCreditCard(speedpayUrl, creditNumber) {
    const response = await Axios.post(speedpayUrl, { DEBIT_ACCOUNT: creditNumber });
    return response.data;
  }
  // Note - this experian API is garbage. Would be great to migrate to something like SmartyStreets
  static async VerifyUSMailingAddress(addressString) {
    const response = await Axios.get(EXPERIAN_URL + "/capture/v1/verify-address/text/?query=" + encodeURIComponent(addressString) + "&country=USA", { headers: { "Auth-Token": EXPERIAN_TOKEN } });
    return response.data;
  }
  // static async GetVerifiedMailingAddress(experianUrl) {
  //   const response = await Axios.get(EXPERIAN_URL + experianUrl, { headers: { "Auth-Token": EXPERIAN_TOKEN } });
  //   return response.data;
  // }

  static async GetStatuspageSummary() {
    const response = await Axios.get(EnvironmentConstants.StatusURL + "/summary.json");
    return response.data;
  }

  static async GetStatuspageScheduled() {
    const response = await Axios.get(EnvironmentConstants.StatusURL + "/scheduled-maintenances/upcoming.json");
    return response.data;
  }

  static async GetWebchatStatus(queueName) {
    const response = await Axios.post(WEBCHAT_URL + "/GetQueueStatus", { QueueNumber: queueName });
    return response.data;
  }
}